/* =============================================================================
   VIEWS / ADMIN / SHRINKAGE-ADMIN — Soll-Ist-Schwund (Phase E5, S164)
   =============================================================================
   Subtab „Schwund" der Gruppe „Ware". Bewegungsbasierter Soll-Ist je Zutat über einen
   Zeitraum (RPC shrinkage_report/-totals, Migration phase_123) + €-Bewertung. Inventur-
   Nacharbeit aus dem Report über das bestehende record_inventory (E1, Manager-gated).

   Rein lesend (außer der Inventur-Buchung). Non-destruktive Renders; S161-Live-View-
   Registry (ein Verkauf ändert den Soll-Verbrauch). Inline-Handler ohne window.func().
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import { formatStock } from '../../core/stock.js';
import { formatShrinkValue, formatShrinkPct, monthRange } from '../../core/shrinkage.js';
import { registerLiveView } from '../../core/live-view-registry.js';

let _period = null;     // { from, to } ISO-Datumsstrings
let _rows = [];         // shrinkage_report()-Zeilen (camelCase)
let _totals = null;     // shrinkage_totals()
let _byId = {};         // ingredientId → Zeile
let _inv = null;        // Inventur-Draft

function flagOn() {
  return !!(window.AppConfig && window.AppConfig.features && window.AppConfig.features.ingredientsDb);
}
function storage() { return window.App && window.App.storage; }

let _shrinkLiveRegistered = false;
function ensureShrinkLiveRegistered() {
  if (_shrinkLiveRegistered) return;
  _shrinkLiveRegistered = true;
  registerLiveView({ tabId: 'tab-shrinkage', render: renderShrinkageAdmin,
    events: ['order:realtime', 'order:status_changed'] });
}

// -----------------------------------------------------------------------------
// Tab-Render
// -----------------------------------------------------------------------------
export async function renderShrinkageAdmin() {
  ensureShrinkLiveRegistered();
  const el = document.getElementById('tab-shrinkage');
  if (!el) return;
  if (!flagOn()) {
    el.innerHTML = `<div class="card"><p class="hint">Der Schwund-Report ist deaktiviert (Feature-Flag <code>ingredientsDb</code>).</p></div>`;
    return;
  }
  if (!_period) _period = monthRange(new Date()); // Default: laufender Monat
  el.innerHTML = `<div class="card"><p class="hint">Lade Schwund-Report…</p></div>`;
  try {
    [_rows, _totals] = await Promise.all([
      storage().shrinkage.report(_period.from, _period.to),
      storage().shrinkage.totals(_period.from, _period.to)
    ]);
  } catch (e) {
    console.warn('[shrinkage] load:', e);
    _rows = []; _totals = null;
  }
  _byId = Object.fromEntries(_rows.map(r => [r.ingredientId, r]));
  el.innerHTML = renderHtml();
}

function renderHtml() {
  return `
    <div class="card">
      <div style="display:flex; justify-content:space-between; align-items:flex-end; gap:12px; flex-wrap:wrap;">
        <div>
          <h2 style="margin:0;">Soll-Ist-Schwund</h2>
          <p class="hint" style="margin:4px 0 0;">Ist-Verbrauch (Lager-Abgänge) gegen Soll-Verbrauch (Verkäufe). Schwund = Verschnitt/Schwund/Korrekturen/Inventur. ${_rows.length} Zutat(en) mit Bewegung.</p>
        </div>
        <div style="display:flex; gap:8px; align-items:flex-end; flex-wrap:wrap;">
          <div class="field" style="margin:0;"><label style="font-size:.78rem;">von</label>
            <input id="shr-from" type="date" value="${htmlEscape(_period.from)}" onchange="shrinkSetPeriod('from', this.value)"></div>
          <div class="field" style="margin:0;"><label style="font-size:.78rem;">bis</label>
            <input id="shr-to" type="date" value="${htmlEscape(_period.to)}" onchange="shrinkSetPeriod('to', this.value)"></div>
          <button class="btn btn-secondary btn-sm" onclick="shrinkApply()">Anzeigen</button>
        </div>
      </div>
      ${renderTotalsHtml()}
      <div style="margin-top:14px; overflow-x:auto;">
        <table style="width:100%; border-collapse:collapse; font-size:.88rem;">
          <thead><tr style="text-align:left; color:var(--ink-soft);">
            <th style="padding:6px 8px;">Zutat</th>
            <th style="padding:6px 8px; text-align:right;" title="Soll-Verbrauch laut Verkäufen (|Verkaufs-Abgänge|)">Soll</th>
            <th style="padding:6px 8px; text-align:right;" title="Ist-Verbrauch = Anfangsbestand + Eingang − Endbestand">Ist</th>
            <th style="padding:6px 8px; text-align:right;" title="Schwund = Ist − Soll (Verschnitt/Schwund/Korrektur/Inventur über die Verkäufe hinaus)">Schwund</th>
            <th style="padding:6px 8px; text-align:right;" title="Schwund / Soll × 100">%</th>
            <th style="padding:6px 8px; text-align:right;" title="Schwund × roher Einkaufspreis je Basiseinheit">€-Verlust</th>
            <th style="padding:6px 8px;"></th>
          </tr></thead>
          <tbody>${_rows.map(renderRowHtml).join('') || '<tr><td colspan="7" class="hint" style="padding:16px; text-align:center;">Keine Bewegungen im Zeitraum.</td></tr>'}</tbody>
        </table>
      </div>
    </div>`;
}

function renderTotalsHtml() {
  if (!_totals) return '';
  const unvalued = Number(_totals.unvaluedCount) > 0
    ? `<span class="hint" style="font-size:.82rem;">${_totals.unvaluedCount} Zutat(en) ohne Einkaufspreis — nicht bewertet</span>` : '';
  const neg = Number(_totals.totalShrinkValueEur) < 0;
  return `
    <div style="margin-top:14px; padding:12px 16px; border-radius:10px; background:var(--bg-soft); border:1px solid var(--border); display:flex; gap:28px; align-items:baseline; flex-wrap:wrap;">
      <div>
        <div class="hint" style="font-size:.78rem; text-transform:uppercase; letter-spacing:.04em;">Gesamt-€-Verlust (Zeitraum)</div>
        <div style="font-size:1.5rem; font-weight:700; ${neg ? 'color:var(--green);' : 'color:var(--danger);'}">${formatShrinkValue(_totals.totalShrinkValueEur)}</div>
        <div class="hint" style="font-size:.72rem;">Schwund × roher Einkaufspreis${neg ? ' · negativ = Mehrbestand' : ''}</div>
      </div>
      <div style="margin-left:auto; align-self:center;">${unvalued}</div>
    </div>`;
}

function renderRowHtml(r) {
  const neg = Number(r.shrinkBase) < 0;
  const shrinkStyle = neg ? 'color:var(--green);' : (Number(r.shrinkBase) > 0 ? 'color:var(--danger); font-weight:600;' : '');
  const valStyle = Number(r.shrinkValueEur) < 0 ? 'color:var(--green);' : (Number(r.shrinkValueEur) > 0 ? 'color:var(--danger); font-weight:600;' : '');
  // Aufschlüsselung als Tooltip: Schwund-Buchung / Korrektur / Inventur (Basiseinheiten).
  const breakdown = `Schwund-Buchung ${formatStock(r.wasteBase, r.baseUnit)} · Korrektur ${formatStock(r.correctionBase, r.baseUnit)} · Inventur ${formatStock(r.inventoryBase, r.baseUnit)} · Bestand (rechn.) ${formatStock(r.closingBase, r.baseUnit)}`;
  return `
    <tr style="border-top:1px solid var(--border);">
      <td style="padding:6px 8px; font-weight:600;">${htmlEscape(r.name)}</td>
      <td style="padding:6px 8px; text-align:right; white-space:nowrap;">${formatStock(r.sollBase, r.baseUnit)}</td>
      <td style="padding:6px 8px; text-align:right; white-space:nowrap;">${formatStock(r.istBase, r.baseUnit)}</td>
      <td style="padding:6px 8px; text-align:right; white-space:nowrap; ${shrinkStyle}" title="${htmlEscape(breakdown)}">${formatStock(r.shrinkBase, r.baseUnit)}</td>
      <td style="padding:6px 8px; text-align:right; white-space:nowrap;">${formatShrinkPct(r.shrinkPct)}</td>
      <td style="padding:6px 8px; text-align:right; white-space:nowrap; ${valStyle}">${formatShrinkValue(r.shrinkValueEur)}</td>
      <td style="padding:6px 8px; text-align:right; white-space:nowrap;">
        <button class="btn btn-ghost btn-sm" onclick="shrinkOpenInventory('${htmlEscape(r.ingredientId)}')" title="Zählung erfassen, deckt versteckten Schwund auf">Inventur</button>
      </td>
    </tr>`;
}

// -----------------------------------------------------------------------------
// Zeitraum
// -----------------------------------------------------------------------------
export function shrinkSetPeriod(field, value) {
  if (!_period) _period = {};
  _period[field] = value;
}
export async function shrinkApply() {
  if (_period && _period.from && _period.to && _period.to < _period.from) {
    showToast('„bis" darf nicht vor „von" liegen', 'error'); return;
  }
  await renderShrinkageAdmin();
}

// -----------------------------------------------------------------------------
// Inventur-Nacharbeit (record_inventory aus E1)
// -----------------------------------------------------------------------------
export function shrinkOpenInventory(ingredientId) {
  const r = _byId[ingredientId];
  if (!r) return;
  _inv = { ingredientId, counted: '', note: '' };
  const overlay = document.getElementById('modal-overlay');
  const content = document.getElementById('modal-content');
  if (!content || !overlay) return;
  const baseLabel = r.baseUnit === 'piece' ? 'Stück' : (r.baseUnit || 'Basis');
  content.innerHTML = `
    <div class="modal-header"><h2>Inventur · ${htmlEscape(r.name)}</h2><button class="modal-close" onclick="shrinkCancelInventory()">×</button></div>
    <p class="hint" style="margin:0 0 10px;">Rechnerischer Bestand (Stand bis-Datum): <strong>${formatStock(r.closingBase, r.baseUnit)}</strong>. Trage den <strong>gezählten</strong> Bestand ein — die Differenz wird als Inventur-Bewegung gebucht und im Schwund sichtbar.</p>
    <div class="field-row">
      <div class="field"><label>Gezählter Bestand (${htmlEscape(baseLabel)})</label>
        <input id="shr-counted" type="number" step="0.01" min="0" oninput="shrinkInvSet('counted', this.value)"></div>
      <div class="field"><label>Notiz (optional)</label>
        <input type="text" oninput="shrinkInvSet('note', this.value)" placeholder="z. B. Inventur 06/2026"></div>
    </div>
    <div class="btn-row" style="margin-top:14px;">
      <button class="btn btn-primary" onclick="shrinkSaveInventory()">Inventur buchen</button>
      <button class="btn btn-ghost" onclick="shrinkCancelInventory()">Abbrechen</button>
    </div>`;
  overlay.classList.add('active');
}

export function shrinkInvSet(field, value) { if (_inv) _inv[field] = value; }

export async function shrinkSaveInventory() {
  if (!_inv) return;
  const counted = Number(_inv.counted);
  if (!isFinite(counted) || counted < 0) { showToast('Gezählter Bestand muss ≥ 0 sein', 'error'); return; }
  try {
    await storage().stock.recordInventory(_inv.ingredientId, counted, _inv.note);
    showToast('Inventur gebucht — Schwund aktualisiert', 'success');
    _inv = null;
    if (typeof window.closeModal === 'function') window.closeModal();
    await renderShrinkageAdmin();
  } catch (e) {
    console.error('[shrinkage] inventory:', e);
    showToast('Buchen fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

export function shrinkCancelInventory() {
  _inv = null;
  if (typeof window.closeModal === 'function') window.closeModal();
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderShrinkageAdmin, shrinkSetPeriod, shrinkApply,
    shrinkOpenInventory, shrinkInvSet, shrinkSaveInventory, shrinkCancelInventory
  });
}
